import { computed, ComputedRef, reactive } from "vue";
import axios from "axios";

import useNotify from "@Composables/useNotify";
import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";
import { ParseServerError, ProcessServerErrors } from "@Modules/shared/Api/ServerErrors";
import TeamUserModel, { TeamUser } from "@Modules/teams/Data/Models/TeamUserModel";

/**
 * Wire shapes returned by the organization/team user endpoints. These mirror the Laravel API
 * resources: OrganizationUserResource and MemberResource.
 */
type OrgUserRow = {
    name: string,
    email: string,
    photo_url: string,
    role: { id: number, name: string, created_at?: string | null, updated_at?: string | null },
};

type TeamMemberRow = {
    name: string,
    email: string,
    photo_url: string,
    role: string,
    status: string | null,
    created_at?: string | null,
    updated_at?: string | null,
};

export type UsersApi = {
    users: ComputedRef<TeamUser[]>,
    loading: ComputedRef<boolean>,
    errors: ComputedRef<Record<string, string>>,
    fetchOrgUsers: (args: { orgID: string, clear?: boolean }) => Promise<void>,
    fetchTeamUsers: (args: { orgID: string, teamID?: string | null, clear?: boolean }) => Promise<void>,
    removeOrgUser: (args: { orgID: string, email: string }) => Promise<boolean>,
    setRouter: (router: HttpRouter<"get" | "post" | "delete">) => UsersApi,
    clearUsers: () => void,
    clearErrors: () => void,
}

/**
 * UsersApi talks to the organization and team member endpoints. Pending invitations are a separate
 * resource owned by InvitationsApi (resources/js/modules/teams/Api/InvitationsApi.ts).
 */
export default function UsersApi(): UsersApi {
    const state = reactive<{
        users: TeamUser[],
        loading: boolean,
        errors: Record<string, string>,
    }>({
        users: [],
        loading: false,
        errors: {},
    });

    let router: HttpRouter<"get" | "post" | "delete"> = axios;
    const { notifySuccess, notifyError } = useNotify();

    function clearErrors() {
        Object.keys(state.errors).forEach((key) => delete state.errors[key]);
    }

    const that = {
        users: computed(() => state.users),
        loading: computed(() => state.loading),
        errors: computed(() => state.errors),
        clearUsers: () => state.users = [],
        clearErrors,

        /**
         * Fetch all users that belong to the provided organization and/or team
         */
        fetchOrgUsers: async function ({
            orgID,
            clear = true,
        }: { orgID: string, teamID?: string | null, clear?: boolean }) {
            clearErrors();
            if (clear) {
                state.users = [];
            }

            state.loading = true;
            const url = `/app/api/organization/${orgID}/users`;
            const response = await router.get<{ data?: OrgUserRow[] }>(url);

            if (response.status < 200 || response.status >= 300) {
                throw new Error(`Could not fetch users for org ${orgID}`);
            }

            const rawUsers = response.data?.data || [];
            state.users = rawUsers.map((user: OrgUserRow) => TeamUserModel({
                ...user,
                role: user.role?.name || "",
                status: null, // The org users endpoint does not return a status; TeamUserData requires the field.
                updated_at: user.role?.updated_at || null,
                created_at: user.role?.created_at || null,
                last_active: user.role?.created_at || null, // FIXME: reusing created_at since last_active is not supported.
            }));
            state.loading = false;
        },

        /**
         * Fetch a list of users that belong to the provided team
         */
        fetchTeamUsers: async function ({
            orgID,
            teamID,
            clear = false,
        }: { orgID: string, teamID?: string | null, clear?: boolean }) {
            clearErrors();
            if (clear) {
                state.users = [];
            }

            if (!teamID || teamID === "personal") {
                state.users = [];
                return;
            }

            state.loading = true;
            const url = `/app/api/organization/${orgID}${teamID ? ("/team/" + teamID) : ""}/members`;
            const response = await router.get<{ data?: TeamMemberRow[] }>(url);

            if (response.status < 200 || response.status >= 300) {
                throw new Error(`Could not fetch members for org ${orgID} team ${teamID}`);
            }

            state.users = (response.data?.data || []).map(
                (user: TeamMemberRow) => TeamUserModel(user),
            );
            state.loading = false;
        },

        /**
         * Remove a user from the organization. Resolves true when the user was removed and
         * false when the request failed, so callers can keep a confirmation open on failure.
         */
        removeOrgUser: async function ({
            orgID,
            email,
        }: { orgID: string, email: string }): Promise<boolean> {
            clearErrors();
            state.loading = true;
            const url = `/app/api/organization/${orgID}/user/${email}`;

            try {
                const response = await router.delete(url);

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not remove user ${email}`);
                }

                state.users = state.users.filter((user) => user.email !== email);
                notifySuccess({ text: "User has been deactivated." });

                return true;
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e);

                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors);
                } else {
                    notifyError({ text: serverMessage || "Failed to deactivate user." });
                }

                return false;
            } finally {
                state.loading = false;
            }
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter: HttpRouter<"get" | "post" | "delete">) {
            router = newRouter;
            return that;
        },
    };

    return that;
}
